<template>
  <fieldset :class="{ 'app-embedded-fieldset': embedded }">
    <legend>
      <span>Email</span>
    </legend>
    <div class="app-contact-inputs">
      <ValidationForm ref="emailForm" >
        <Field name="email address" v-slot="{  errors }" rules="email|max:80">
          <div class="app-input-group app-contact-info" :class="{ 'app-invalid-group': errors.length > 0 || invalidEmailChars }"
               ref="emailGroup">
            <label for="userEmail" class="sr-only">Enter your email address</label>
            <input class="app-user-email" 
              id="userEmail" 
              ref="userEmail" 
              type="email"
              v-model="myEmail"               
              @blur="checkEmail"
              :readonly="editable"
              :disabled="editable"
            >
            <p v-if="invalidEmailChars" ref="invalidChar">Email address contains invalid characters.</p>
            <p v-else ref="invalidEmail">{{ errors[0] }}</p>
          </div>
        </Field>
      </ValidationForm>
    </div>
  </fieldset>
</template>


<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { max as maxRule, email as emailRule } from '@vee-validate/rules'; 
import { defineRule, Form as ValidationForm, Field  } from 'vee-validate';


defineRule('email', (value, _, ctx) => {
  const valid = emailRule(value);
  if (!valid) {
    return `Please enter a valid ${ctx.field}.`
  }
  return true;
})

defineRule('max', (value, [limit], ctx) => {
  const isValid = maxRule(value, [limit]);
  if (!isValid) {
    return `${ctx.field} is limited to ${limit} characters.`
  }
  return true;
})

const props = defineProps({
  embedded: {
    type: Boolean,
    required: false,
    default: false,
  },
  isModal: {
    type: Boolean,
    required: false,
    default: false,
  },
  verify: {
    type: Boolean,
    required: false,
    default: false,
  },
  editable: {
    type: Boolean,
    required: false,
    default: false,
  }
});

// Emits
const emit = defineEmits(['doResetVerify', 'doEmail']);

// Vuex store
const store = useStore(); 

// Reactive refs
const myEmail = ref('');
const emailForm = ref(null);

// Computed props
const invalidEmailChars = computed(() => {
  const pattern = /^[a-zA-Z0-9@._-]*$/;
  return !pattern.test(myEmail.value);
});

// Watchers
watch(myEmail, () => {
  checkEmail();
});

watch(() => props.verify, (newVal) => {
  if (newVal) {
    checkEmail();
  }
});

// Lifecycle hooks
onMounted(() => {
  // Pre-fill if info is available
  const resDetails = store.state.user.resDetails;
  const customerDetail = store.state.user.customerDetail;

  if (resDetails && resDetails.userInfo) {
    const info = resDetails.userInfo.reservationSendConfirmationRequest;
    if (info && info.email && info.email.length > 0) {
      myEmail.value = info.email;
    }
  }

  if (customerDetail) {
    const info = customerDetail;
    if (info && info.email && info.email.length > 0) {
      myEmail.value = info.email;
    }
  }
});

// Methods
const checkEmail = async () => {
  const { valid } = await emailForm.value.validate();
  const hasError = !valid || (myEmail.value.length > 0 && invalidEmailChars.value);

  if (hasError) {
    emit('doResetVerify');
  }

  // isValid is only used by ModalContact (not UserContact)
  emit('doEmail', { email: myEmail.value, isValid: !hasError });
};

defineExpose({
  // userEmail: exposedUserEmail,
  myEmail,
  checkEmail,
  invalidEmailChars, 
});
</script>


<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/contact";
</style>
